import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { useApi } from "@/lib/api";

/**
 * Cited sites report: which domains AI answers cite across every stored
 * answer (tracked search runs, leader lookups, guide lookups). Used to find
 * the directories and listicles worth getting creators onto.
 */

const ENGINE_LABELS = {
    google_ai_mode: "AI Mode",
    bing_copilot: "Copilot",
    chatgpt: "ChatGPT",
    gemini: "Gemini",
};

const SOURCE_LABELS = {
    tracked_search_runs: "tracked",
    leader_lookups: "leaders",
    guide_lookups: "guides",
};

export default function CitedSitesPanel() {
    const { request } = useApi();
    const [city, setCity] = useState("");
    const [niche, setNiche] = useState("");
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = new URLSearchParams({ days: String(days) });
            if (city) qs.set("city", city);
            if (niche) qs.set("niche", niche);
            const res = await request(`/admin/cited-sites?${qs.toString()}`);
            setData(res);
        } catch (err) {
            toast.error(err?.message || "Could not load cited sites.");
        } finally {
            setLoading(false);
        }
    }, [request, city, niche, days]);

    useEffect(() => {
        void load();
    }, [load]);

    const domains = data?.domains || [];

    return (
        <section data-testid="admin-cited-sites" className="mt-10">
            <header className="mb-4">
                <h2 className="font-display text-2xl font-bold tracking-tight">
                    Cited sites
                </h2>
                <p className="text-sm text-black/60 mt-1">
                    Domains AI answers cite, counted once per answer. Directories and
                    listicles near the top are where creators should be listed.
                </p>
            </header>
            <div className="flex flex-wrap gap-2 mb-3">
                <select
                    data-testid="admin-cited-sites-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="samoony-input !h-9"
                >
                    <option value="">Every city</option>
                    {(data?.cities || []).map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </select>
                <select
                    data-testid="admin-cited-sites-niche"
                    value={niche}
                    onChange={(e) => setNiche(e.target.value)}
                    className="samoony-input !h-9"
                >
                    <option value="">Every niche</option>
                    {(data?.niches || []).map((n) => (
                        <option key={n} value={n}>
                            {n}
                        </option>
                    ))}
                </select>
                <select
                    data-testid="admin-cited-sites-days"
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className="samoony-input !h-9"
                >
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={365}>Last year</option>
                </select>
                {data && (
                    <span className="self-center text-xs text-black/50">
                        {data.answers} answers scanned
                    </span>
                )}
            </div>
            <div className="bg-white border border-black/10 rounded-2xl divide-y divide-black/10">
                {loading && <div className="p-4 text-sm text-black/50">Loading...</div>}
                {!loading && domains.length === 0 && (
                    <div className="p-6 text-sm text-black/60">
                        No cited sites in this window.
                    </div>
                )}
                {!loading &&
                    domains.map((d) => (
                        <div key={d.domain} data-testid={`admin-cited-site-${d.domain}`}>
                            <button
                                type="button"
                                onClick={() => setOpen(open === d.domain ? null : d.domain)}
                                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-black/[0.03]"
                            >
                                <div className="min-w-0">
                                    <div className="font-mono text-sm text-black/80 truncate">
                                        {d.domain}
                                    </div>
                                    <div className="text-xs text-black/60">
                                        {Object.entries(d.engines)
                                            .map(([e, n]) => `${ENGINE_LABELS[e] || e} ${n}`)
                                            .join(" · ")}
                                        {"  |  "}
                                        {Object.entries(d.sources)
                                            .map(([s, n]) => `${SOURCE_LABELS[s] || s} ${n}`)
                                            .join(" · ")}
                                    </div>
                                </div>
                                <span className="font-mono text-sm text-black/80 shrink-0">
                                    {d.answers}
                                </span>
                            </button>
                            {open === d.domain && (
                                <div className="px-4 pb-4 text-xs text-black/70 space-y-2">
                                    <div>
                                        <div className="font-semibold mb-1">Top URLs</div>
                                        <ul className="space-y-0.5">
                                            {d.urls.map((u) => (
                                                <li key={u.url} className="truncate">
                                                    <a
                                                        href={u.url}
                                                        target="_blank"
                                                        rel="noreferrer noopener"
                                                        className="underline"
                                                    >
                                                        {u.url}
                                                    </a>{" "}
                                                    <span className="text-black/40">x{u.count}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {d.questions.length > 0 && (
                                        <div>
                                            <div className="font-semibold mb-1">Cited for</div>
                                            <ul className="space-y-0.5">
                                                {d.questions.map((q) => (
                                                    <li key={q}>{q}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
            </div>
        </section>
    );
}
